import React, { useMemo, useState } from 'react';
import { useGasI18n, useGasNotify } from '../../shared/kit';
import type { PlanDetailInitData, PlanInit, SubscribeConfig } from './types';

interface Props {
    initData: PlanDetailInitData;
}

const NAME_YOUR_PRICE_STEP = 0.05;

/** Signature i18n du kit : t(clé, params?, défaut?). */
type TFn = (key: string, params?: Record<string, string | number>, fallback?: string) => string;

/** true si le plan est en paiement conscient (le membre choisit son montant récurrent). */
function isNameYourPricePlan(plan: PlanInit): boolean {
    return plan.price_mode === 'name_your_price';
}

/**
 * Bornes Name Your Price résolues côté client (purement indicatives — le serveur
 * re-borne toujours). `min` ne descend jamais sous 0 ; `suggested` est ramené
 * dans [min, max] s'il existe.
 */
function resolveNameYourPrice(plan: PlanInit): { min: number; max: number | null; suggested: number } {
    const min = Math.max(0, Number(plan.nyp_min ?? 0) || 0);
    const max =
        plan.nyp_max !== null && plan.nyp_max !== undefined ? Math.max(min, Number(plan.nyp_max)) : null;
    let suggested = Number(plan.nyp_suggested ?? 0) || 0;
    if (suggested < min) {
        suggested = min;
    }
    if (max !== null && suggested > max) {
        suggested = max;
    }
    return { min, max, suggested };
}

/** Présélections de montant : suggested, ×2, ×5, bornées au plafond du plan s'il existe. */
function buildNameYourPricePresets(suggested: number, max: number | null): number[] {
    const base = suggested > 0 ? suggested : 10;
    const raw = [base, base * 2, base * 5];
    const filtered = raw.filter((v) => max === null || v <= max);
    return Array.from(new Set(filtered.map((v) => Math.round(v * 100) / 100)));
}

/**
 * Démarre l'abonnement récurrent. REST en priorité
 * (POST gas/v1/membership/me/subscriptions/subscribe), fallback AJAX
 * (gas_membership_user_subscribe) si la config REST est absente ou si le
 * transport REST échoue (5xx/réseau). Retourne l'URL de redirection Stripe.
 */
async function requestSubscribe(
    subscribe: SubscribeConfig,
    planId: number,
    amount: number | null,
): Promise<string | null> {
    const ajax = async (): Promise<string | null> => {
        const body = new FormData();
        body.append('action', 'gas_membership_user_subscribe');
        body.append('nonce', subscribe.nonce);
        body.append('plan_id', String(planId));
        // Paiement conscient : montant choisi par le membre. Le serveur le
        // re-borne toujours dans [nyp_min, nyp_max] — pas besoin de sécuriser ici.
        if (amount !== null && Number.isFinite(amount)) {
            body.append('amount', String(amount));
        }
        const res = await fetch(subscribe.ajax_url, { method: 'POST', credentials: 'same-origin', body });
        const json = await res.json();
        if (json?.success && json?.data?.redirect) {
            return json.data.redirect as string;
        }
        throw new Error(json?.data?.message || '');
    };

    const canRest = subscribe.api_transport !== 'ajax' && String(subscribe.rest_nonce || '').trim() !== '';
    if (canRest) {
        const base = String(subscribe.rest_base_url || '/wp-json/gas/v1').replace(/\/+$/, '');
        const restBody: { plan_id: number; amount?: number } = { plan_id: planId };
        if (amount !== null && Number.isFinite(amount)) {
            restBody.amount = amount;
        }
        try {
            const res = await fetch(`${base}/membership/me/subscriptions/subscribe`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': String(subscribe.rest_nonce || ''),
                },
                body: JSON.stringify(restBody),
            });
            const json = await res.json().catch(() => null);
            if (res.ok && json?.data?.redirect) {
                return json.data.redirect as string;
            }
            // Erreur métier (4xx) : message explicite, pas de fallback.
            if (res.status >= 400 && res.status < 500) {
                throw new Error((json && (json.message as string)) || '');
            }
            // 5xx → fallback AJAX
        } catch (e) {
            if (e instanceof Error && e.message) {
                throw e;
            }
            // erreur réseau → fallback AJAX
        }
    }
    return ajax();
}

export default function App({ initData }: Props): React.ReactElement {
    const { t } = useGasI18n();
    const notify = useGasNotify();
    const { plan, buy_url, plans_url, subscribe } = initData;
    const [subscribing, setSubscribing] = useState(false);

    const isNameYourPrice = isNameYourPricePlan(plan);
    const nyp = useMemo(() => resolveNameYourPrice(plan), [plan]);
    // Montant Name Your Price choisi (chaîne pour piloter un <input> contrôlé).
    // Pré-rempli avec le montant suggéré.
    const [nameYourPriceAmount, setNameYourPriceAmount] = useState<string>(() =>
        isNameYourPrice && nyp.suggested > 0 ? String(nyp.suggested) : '',
    );

    const handleSubscribe = async (): Promise<void> => {
        if (subscribing) {
            return;
        }
        // Paiement conscient : transmet le montant choisi (null si fixe).
        let amount: number | null = null;
        if (isNameYourPrice) {
            const raw = Number(nameYourPriceAmount);
            amount = Number.isFinite(raw) && raw >= 0 ? raw : null;
        }
        setSubscribing(true);
        try {
            const redirect = await requestSubscribe(subscribe, plan.id, amount);
            if (redirect) {
                window.location.href = redirect;
                return;
            }
            notify.error(t('errors.generic'));
        } catch (e) {
            notify.error((e as Error)?.message || t('errors.generic'));
        } finally {
            setSubscribing(false);
        }
    };
    // Réutilise les libellés existants membership.plan.period_kinds.*
    const periodLabel = t(`membership.plan.period_kinds.${plan.period_kind}`);

    const chfFormatter = useMemo(
        () =>
            new Intl.NumberFormat(initData.locale === 'en' ? 'en-CH' : 'fr-CH', {
                style: 'currency',
                currency: 'CHF',
            }),
        [initData.locale],
    );

    // En paiement conscient, on n'affiche pas le prix fixe du produit lié :
    // le montant est choisi par le membre ci-dessous.
    const priceLabel =
        !isNameYourPrice && plan.price !== null && plan.price !== undefined
            ? chfFormatter.format(plan.price)
            : null;

    // Description : HTML riche déjà assaini côté serveur via wp_kses_post (modèle + controller).
    const descriptionMarkup = { __html: plan.description };
    const benefitLines = plan.benefits.length > 0
        ? plan.benefits
        : plan.capabilities.map((cap) => t(cap.label_key));
    const subscribeLabel = subscribe.enabled
        ? t('membership.plan.subscribe_oneshot')
        : t('membership.plan.subscribe');

    return (
        <article className="gas-membership-plan">
            <nav className="gas-membership-plan__breadcrumb" aria-label={t('membership.plan.breadcrumb_label')}>
                <a className="gas-membership-plan__breadcrumb-link" href={plans_url}>
                    <span aria-hidden="true">←</span> {t('membership.plan.see_all')}
                </a>
            </nav>

            <header className="gas-membership-plan__hero">
                <div className="gas-membership-plan__hero-copy">
                    <span className="gas-membership-plan__eyebrow">{t('membership.plan.public_eyebrow')}</span>
                    <h1 className="gas-membership-plan__title">{plan.name}</h1>
                    {plan.intro && <p className="gas-membership-plan__intro">{plan.intro}</p>}
                    <div className="gas-membership-plan__meta">
                        {priceLabel && <span className="gas-membership-plan__price">{priceLabel}</span>}
                        {isNameYourPrice && (
                            <span className="gas-membership-plan__price gas-membership-plan__price--nyp">
                                {t('membership.subscribe.price_mode_label')}
                            </span>
                        )}
                        <span className="gas-membership-plan__period">{periodLabel}</span>
                        {plan.is_recurring && (
                            <span className="gas-membership-plan__badge">{t('membership.plan.recurring')}</span>
                        )}
                    </div>
                </div>

                {plan.image_url && (
                    <figure className="gas-membership-plan__media">
                        <img className="gas-membership-plan__image" src={plan.image_url} alt={plan.name} />
                    </figure>
                )}
            </header>

            <div className="gas-membership-plan__layout">
                <main className="gas-membership-plan__content">
                    {plan.description && (
                        <section className="gas-membership-plan__section" aria-labelledby="gas-membership-plan-overview">
                            <p className="gas-membership-plan__section-kicker">{t('membership.plan.overview_kicker')}</p>
                            <h2 id="gas-membership-plan-overview" className="gas-membership-plan__section-title">
                                {t('membership.plan.overview_title', { name: plan.name })}
                            </h2>
                            <div
                                className="gas-membership-plan__description"
                                dangerouslySetInnerHTML={descriptionMarkup}
                            />
                        </section>
                    )}

                    {benefitLines.length > 0 && (
                        <section className="gas-membership-plan__section gas-membership-plan__benefits" aria-labelledby="gas-membership-plan-benefits">
                            <p className="gas-membership-plan__section-kicker">{t('membership.plan.benefits_kicker')}</p>
                            <h2 id="gas-membership-plan-benefits" className="gas-membership-plan__section-title">
                                {t('membership.plan.benefits_title')}
                            </h2>
                            <ul className="gas-membership-plan__features">
                                {benefitLines.map((benefit) => (
                                    <li key={benefit} className="gas-membership-plan__feature">
                                        <span className="gas-membership-plan__feature-mark" aria-hidden="true">✓</span>
                                        <span>{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </main>

                <aside className="gas-membership-plan__purchase" aria-labelledby="gas-membership-plan-purchase">
                    <p className="gas-membership-plan__purchase-kicker">{t('membership.plan.purchase_kicker')}</p>
                    <h2 id="gas-membership-plan-purchase" className="gas-membership-plan__purchase-title">
                        {t('membership.plan.purchase_title')}
                    </h2>

                    <dl className="gas-membership-plan__summary">
                        {(priceLabel || isNameYourPrice) && (
                            <div className="gas-membership-plan__summary-row">
                                <dt>{t('membership.plan.price_label')}</dt>
                                <dd>{priceLabel || t('membership.subscribe.price_mode_label')}</dd>
                            </div>
                        )}
                        <div className="gas-membership-plan__summary-row">
                            <dt>{t('membership.plan.duration_label')}</dt>
                            <dd>{periodLabel}</dd>
                        </div>
                    </dl>

                    {plan.requires_cotisation && (
                        <p className="gas-membership-plan__notice">{t('membership.plan.requires_cotisation')}</p>
                    )}

                    {subscribe.enabled && isNameYourPrice && (
                        <NameYourPriceChooser
                            min={nyp.min}
                            max={nyp.max}
                            suggested={nyp.suggested}
                            amount={nameYourPriceAmount}
                            onAmountChange={setNameYourPriceAmount}
                            formatChf={(value) => chfFormatter.format(value)}
                            t={t}
                        />
                    )}

                    <div className="gas-membership-plan__actions">
                        {subscribe.enabled && (
                            <button
                                type="button"
                                className="gas-button gas-button--primary gas-membership-plan__cta"
                                onClick={handleSubscribe}
                                disabled={subscribing}
                            >
                                {subscribing ? t('common.loading') : t('membership.plan.subscribe_recurring')}
                            </button>
                        )}
                        {plan.is_purchasable ? (
                            <a
                                className={`gas-button gas-membership-plan__cta ${
                                    subscribe.enabled ? 'gas-button--outline' : 'gas-button--primary'
                                }`}
                                href={buy_url}
                            >
                                {subscribeLabel}
                            </a>
                        ) : (
                            <p className="gas-membership-plan__unavailable">{t('membership.plan.unavailable')}</p>
                        )}
                    </div>
                    {plan.is_purchasable && (
                        <p className="gas-membership-plan__purchase-hint">{t('membership.plan.purchase_hint')}</p>
                    )}
                </aside>
            </div>

            <footer className="gas-membership-plan__closing">
                <div>
                    <p className="gas-membership-plan__closing-title">{t('membership.plan.closing_title')}</p>
                    <p className="gas-membership-plan__closing-text">
                        {t('membership.plan.closing_text', { name: plan.name })}
                    </p>
                </div>
                {plan.is_purchasable && (
                    <a className="gas-button gas-button--primary gas-membership-plan__closing-cta" href={buy_url}>
                        {subscribeLabel}
                    </a>
                )}
            </footer>
        </article>
    );
}

interface NameYourPriceChooserProps {
    min: number;
    max: number | null;
    suggested: number;
    amount: string;
    onAmountChange: (value: string) => void;
    formatChf: (value: number) => string;
    t: TFn;
}

/**
 * Sélecteur de montant pour les plans en paiement conscient (adhésion solidaire).
 * Présente quelques présélections (suggéré / ×2 / ×5) + une saisie libre. Le
 * montant choisi pilote le state parent et part dans `amount` à la souscription.
 * Le serveur re-borne toujours le montant dans [min, max].
 */
function NameYourPriceChooser({
    min,
    max,
    suggested,
    amount,
    onAmountChange,
    formatChf,
    t,
}: NameYourPriceChooserProps): React.ReactElement {
    const presets = buildNameYourPricePresets(suggested, max);
    const currentNum = Number(amount);

    return (
        <div className="gas-membership-plan__nyp">
            <p className="gas-membership-plan__nyp-title">
                {t('membership.subscribe.choose_amount', {}, 'Choisissez votre montant')}
            </p>
            <p className="gas-membership-plan__nyp-hint">
                {min > 0
                    ? t(
                          'membership.subscribe.hint_min',
                          { min: min.toFixed(2) },
                          'Montant libre, minimum %min% CHF par échéance.',
                      )
                    : t('membership.subscribe.hint_free', {}, 'Choisissez librement votre montant par échéance.')}
            </p>

            {presets.length > 0 && (
                <div
                    className="gas-membership-plan__nyp-presets"
                    role="group"
                    aria-label={t('membership.subscribe.presets_label', {}, 'Montants suggérés')}
                >
                    {presets.map((preset) => {
                        const active = Math.abs(currentNum - preset) < 0.005;
                        return (
                            <button
                                key={preset}
                                type="button"
                                className={`gas-membership-plan__nyp-preset${
                                    active ? ' gas-membership-plan__nyp-preset--active' : ''
                                }`}
                                aria-pressed={active}
                                onClick={() => onAmountChange(String(preset))}
                            >
                                {formatChf(preset)}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="gas-membership-plan__nyp-custom">
                <label htmlFor="gas-membership-plan-nyp-amount" className="gas-membership-plan__nyp-label">
                    {t('membership.subscribe.custom_label', {}, 'Autre montant (CHF)')}
                </label>
                <div className="gas-membership-plan__nyp-input-wrap">
                    <input
                        id="gas-membership-plan-nyp-amount"
                        type="number"
                        className="gas-membership-plan__nyp-input"
                        inputMode="decimal"
                        min={min}
                        {...(max !== null ? { max } : {})}
                        step={NAME_YOUR_PRICE_STEP}
                        value={amount}
                        placeholder={suggested > 0 ? suggested.toFixed(2) : '0.00'}
                        onChange={(e) => onAmountChange(e.target.value)}
                    />
                    <span className="gas-membership-plan__nyp-currency">
                        {t('membership.subscribe.currency', {}, 'CHF')}
                    </span>
                </div>
            </div>
        </div>
    );
}
